import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cake, Loader2, CalendarX2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const Person = ({ p, today }) => (
  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-md ${today ? "bg-amber-100/80 ring-1 ring-amber-300" : ""}`} data-testid={`bday-person-${p.row_id}`}>
    <span className="w-9 text-center font-display font-bold text-lg text-amber-600">{p.day}</span>
    <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>{p.extra && <div className="text-xs text-slate-500 truncate">{p.extra}</div>}</div>
    {today && <span className="text-[11px] font-bold text-amber-800 inline-flex items-center gap-1" data-testid={`bday-today-${p.row_id}`}><Cake className="w-3.5 h-3.5" />Today</span>}
  </div>
);

const FieldPicker = ({ d, canEdit, onPick }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 mb-4">
    Birthday field:
    <select disabled={!canEdit} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2" value={d.field_key || ""} onChange={(e) => onPick(e.target.value)} data-testid="bday-field-select">
      {!d.field_key && <option value="">— choose —</option>}
      {d.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
    </select>
  </div>
);

const Undated = ({ list }) => list.length > 0 && (
  <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mt-4" data-testid="bday-undated">
    <div className="font-semibold text-slate-900 flex items-center gap-2 mb-2"><CalendarX2 className="w-4 h-4 text-slate-500" />No date recorded ({list.length})</div>
    <div className="grid sm:grid-cols-2 gap-1">{list.map((p) => (
      <div key={p.row_id} className="text-sm text-slate-700 px-3 py-1.5" data-testid={`bday-undated-${p.row_id}`}>{p.name}{p.raw && <span className="text-xs text-rose-600 ml-2">“{p.raw}”</span>}</div>
    ))}</div>
  </div>
);

export const BirthdayViews = ({ mode, canEdit, refreshKey }) => {
  const [d, setD] = useState(null);
  const load = () => api.get("/birthdays/calendar").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!d.fields.length) return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-600" data-testid="bday-no-fields">Import your birthday file in the Register tab first.</div>;
  const pick = async (key) => { try { await api.put("/birthdays/field", { key }); toast.success("Birthday field updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const now = new Date();
  const m = now.getMonth();
  const isToday = (mi, p) => mi === m && p.day === now.getDate();
  if (mode === "month") {
    const list = [...d.months[m]].sort((a, b) => isToday(m, b) - isToday(m, a) || a.day - b.day);
    return (
      <div>
        <FieldPicker d={d} canEdit={canEdit} onPick={pick} />
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4" data-testid="bday-this-month">
          <div className="flex items-baseline gap-2 mb-3"><span className="font-display text-2xl font-bold text-slate-900">{MONTHS[m]}</span><span className="text-sm text-slate-500" data-testid="bday-this-month-count">{list.length} birthdays</span></div>
          {list.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center" data-testid="bday-this-month-empty">No birthdays recorded this month.</p>
            : <div className="space-y-1">{list.map((p) => <Person key={p.row_id} p={p} today={isToday(m, p)} />)}</div>}
        </div>
        <Undated list={d.undated} />
      </div>
    );
  }
  return (
    <div>
      <FieldPicker d={d} canEdit={canEdit} onPick={pick} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="bday-by-month">
        {d.months.map((list, i) => (
          <div key={i} className={`rounded-lg border bg-white shadow-sm p-4 fade-up ${i === m ? "border-amber-400" : "border-slate-200"}`} style={{ animationDelay: `${i * 30}ms` }} data-testid={`bday-month-${i + 1}`}>
            <div className="flex items-baseline justify-between mb-2"><span className="font-semibold text-slate-900">{MONTHS[i]}</span><span className="text-xs font-semibold text-amber-700" data-testid={`bday-month-count-${i + 1}`}>{list.length}</span></div>
            {list.length === 0 ? <p className="text-xs text-slate-400 py-2">—</p> : <div className="space-y-0.5">{list.map((p) => <Person key={p.row_id} p={p} today={isToday(i, p)} />)}</div>}
          </div>
        ))}
      </div>
      <Undated list={d.undated} />
    </div>
  );
};
